import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import { ModeEditOutlineOutlined } from '@mui/icons-material';
import moment from 'moment';
import { PetParentAnimal } from '../../../types/PetParent';

interface PetProfileViewProps {
  animal: PetParentAnimal;
  onEdit: () => void;
}

function calculateAge(birthDate: string): string {
  if (!birthDate) return 'Not provided';

  const birth = moment(birthDate);
  const now = moment();
  const years = now.diff(birth, 'years');
  const months = now.diff(birth, 'months') % 12;

  if (years >= 1) {
    return `${years} year${years > 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
  }
  return `${months} month${months !== 1 ? 's' : ''}`;
}

function ProfileField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: 'rgba(0, 0, 0, 0.6)',
          mb: 0.75,
          lineHeight: '12px',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
      >
        {value || 'Not provided'}
      </Typography>
    </Box>
  );
}

function PetProfileView({ animal, onEdit }: PetProfileViewProps) {
  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '12px',
        boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 2, py: 1.5, minHeight: '48px',
      }}
      >
        <Typography
          variant="h6"
          sx={{ flexGrow: 1, color: 'rgba(0, 0, 0, 0.87)' }}
        >
          Profile
        </Typography>
        <Button
          variant="text"
          size="large"
          onClick={onEdit}
          startIcon={<ModeEditOutlineOutlined />}
          sx={{ color: '#3700B3' }}
        >
          Edit
        </Button>
      </Box>

      <ProfileField label="Name" value={animal.name} />
      <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

      <ProfileField label="Age" value={calculateAge(animal.birthDate)} />
      <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

      <ProfileField label="Primary breed" value={animal.primaryBreed} />
      <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

      <ProfileField label="Sex" value={animal.gender} />
      <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

      <ProfileField
        label="Adoption date"
        value={animal.exitDate ? moment(animal.exitDate).format('MM/DD/YYYY') : null}
      />
    </Box>
  );
}

export default PetProfileView;
